import React from 'react';
import { ApiError, loadAll } from '../load.js';
import { closeDialog, dialogMode, inTeams, teamsConfigMode, teamsSubPage } from '../teams.js';
import { api, login, logout as doLogout } from '../api.js';
import { mapNotifs } from '../mapping.js';
import { Approval } from './approval.jsx';
import {AppIcon, Avatar, Empty, Icon, StatusPill} from './components.jsx';
import { applyServerData, FEATURES, FIRST_VIEW, APPROVAL_SUMMARY, APPS, APPS_ERROR, ME, NOTIFS, PREFERENCES } from './data.jsx';
import { HEALTH, openService } from './apps.js';
import { AlertRow, Button, LogoMark, Pill, SegmentedControl } from './ds/fgcp-ds.jsx';
import { Home } from './home.jsx';
import { OPS_KIND, opsPeriod } from './ops.js';
import { Leave, Org } from './modules.jsx';
import { TaskView } from './task.jsx';
import { Board } from './board.jsx';
import { Booking } from './booking.jsx';
import { Admin } from './admin.jsx';
import { MailAgent } from './mail-agent.jsx';
import { NotifySettings } from './notify-settings.jsx';
import { usePreferences } from './use-preferences.js';
import { TeamsLab, TeamsTabConfig } from './teams-lab.jsx';

// app.jsx — FGCP Workspace 셸 (product theme)
const WBtn = Button;
const WPill = Pill;
const WLogo = LogoMark;
const WAlert = AlertRow;
const { useState: uS, useEffect: uE } = React;


const NAV = [
  { id: 'home', label: '홈', icon: 'home' },
  { group: '그룹웨어' },
  { id: 'approval', label: '전자결재', icon: 'file-check', badge: 'approval' },
  { id: 'board', label: '게시판·공지', icon: 'megaphone' },
  { id: 'org', label: '조직도·사원', icon: 'users' },
  { id: 'booking', label: '회의실 예약', icon: 'calendar' },
  { id: 'leave', label: '휴가관리', icon: 'umbrella' },
  { id: 'task', label: '내 업무', icon: 'check-square' },
  { id: 'mailagent', label: '메일 에이전트', icon: 'inbox', feature: 'mailAgent' },   // 켤 수 있거나 켠 기능이 있는 사람만(계획 문서 5.6)
  // 서비스 메뉴는 관리 화면에 등록된 실제 서비스만 보인다(가상 서비스는 두지 않음)
  { group: '관리', admin: true },
  { id: 'admin', label: '관리자', icon: 'shield', admin: true },
  // Teams 기능 시험실(계획 문서 5.8) — Teams 탭 안·시스템 관리자만, 사이드바에는 없다(#/teams-lab 또는 봇 '기능 시험'으로)
  { id: 'teams-lab', label: 'Teams 시험실', icon: 'flask-conical', hidden: true, sysAdmin: true, teams: true },
];
const isAdmin = () => (ME.roles || []).some(r => r === 'system_admin' || r === 'workspace_admin');
/** 갈 수 있는 화면인가 — NAV에 있는 id만(관리자 화면은 관리자만). 저장된 값·링크·검색 결과 모두 이것으로 거른다(D8) */
const navVisible = n => (!n.admin || isAdmin()) && (!n.feature || FEATURES[n.feature]) && (!n.sysAdmin || (ME.roles || []).includes('system_admin')) && (!n.teams || inTeams());
const knownView = id => NAV.some(n => n.id === id && navVisible(n));
/** id로 바로 여는 화면(검색 결과·알림 링크) */
const DEEP_VIEWS = ['approval', 'board', 'org', 'booking', 'task'];
const TITLES = { 'teams-lab': 'Teams 시험실', admin: '관리자', mailagent: '메일 에이전트', home: 'Workspace', approval: '전자결재', board: '게시판·공지', org: '조직도·사원', booking: '회의실 예약', leave: '휴가관리', task: '내 업무' };

function App() {
  // 저장된 화면 값은 적재가 끝난 뒤 다시 검사한다(관리자 여부는 그때 안다)
  // 마지막으로 본 화면(브라우저 편의 — 없으면 진입 호스트별 첫 화면을 loadAll 뒤에 고른다)
  const [view, setView] = uS(() => { try { return localStorage.getItem('ws-view') || ''; } catch { return ''; } });
  const [deep, setDeep] = uS(null);   // { view, id } — 그 화면에서 바로 열 항목
  const [launcher, setLauncher] = uS(false);
  const [notifOpen, setNotifOpen] = uS(false);
  const [userMenu, setUserMenu] = uS(false);
  const [notifySettings, setNotifySettings] = uS(false);
  const [search, setSearch] = uS(false);
  const [toast, setToast] = uS('');
  const [homeTick, setHomeTick] = uS(0);
  const [opsNotices, setOpsNotices] = uS([]);   // 지금 보이는 운영 공지(서버가 고른 것 — 상단 띠는 장애·점검만)   // 홈 요약 다시 받기(결재 처리 뒤·60초 주기)
  const [approvalSummary, setApprovalSummary] = uS(APPROVAL_SUMMARY);   // 배지·홈 '결재 대기' — 서버가 센 값
  const [notifs, setNotifs] = uS(NOTIFS);
  // 서버 데이터 적재 — data 모듈 값을 갈아끼운 뒤 rev를 올려 하위 트리를 새로 그린다
  const [rev, setRev] = uS(0);
  const [loadState, setLoadState] = uS('loading');   // loading | ready | error
  const [loadError, setLoadError] = uS('');
  const showToast = React.useCallback(m => { setToast(m); clearTimeout(window.__wt); window.__wt = setTimeout(() => setToast(''), 2600); }, []);
  // 테마·사이드바 — 서버에 저장한 내 설정(Teams 탭과 브라우저가 같은 값)
  const [prefs, setPref, setPrefs] = usePreferences(showToast);

  uE(() => {
    let alive = true;
    loadAll()
      .then(() => {
        if (!alive) return;
        setApprovalSummary(APPROVAL_SUMMARY); setNotifs(NOTIFS); setPrefs(PREFERENCES);
        setView(v => knownView(v) ? v : FIRST_VIEW);   // 첫 화면 — 서버가 진입 호스트별로 정한 값(/me firstView), 링크(#/…)가 있으면 그것이 먼저
        setRev(r => r + 1); setLoadState('ready');
      })
      .catch((err) => {
        if (!alive) return;
        if (err instanceof ApiError && err.status === 401 && !inTeams()) { login(); return; }   // 미인증 → Entra 로그인
        if (inTeams()) {
          // Teams 안: 토큰을 못 받았거나(401) 이 계정이 Workspace를 쓸 수 없는 경우
          setLoadError(err instanceof ApiError && err.status === 401
            ? 'Teams 계정으로 Workspace를 열 수 없습니다. 조직도에 배정되지 않았거나 사용이 막힌 계정일 수 있습니다.'
            : `Teams 로그인 정보를 받지 못했습니다 (${err?.message ?? err}). 브라우저에서 Workspace를 열어 주세요.`);
          setLoadState('error');
          return;
        }
        setLoadError(err?.message ?? '데이터를 불러오지 못했습니다');
        setLoadState('error');
      });
    return () => { alive = false; };
  }, []);

  uE(() => { try { localStorage.setItem('ws-view', view); } catch { /* 저장소를 못 쓰면 다음에 홈에서 시작 */ } }, [view]);
  uE(() => { document.documentElement.setAttribute('data-mode', prefs.theme); }, [prefs.theme]);
  uE(() => {
    const onKey = e => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); setSearch(true); }
      if (e.key === 'Escape') { setSearch(false); setLauncher(false); setNotifOpen(false); setUserMenu(false); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // 결재를 처리·상신한 뒤 배지·홈 값을 서버에서 다시 받는다(화면에서 셈하지 않는다). 처리는 이미 끝났으므로 실패는 알리기만 한다
  const refreshApprovals = () => api.get('/api/v1/approvals/summary')
    .then(summary => { setApprovalSummary(summary); setHomeTick(n => n + 1); })
    .catch(e => showToast('결재 건수를 새로 받지 못했습니다 — ' + e.message));
  const nav = id => {
    if (id === '__launcher') { setLauncher(true); return; }
    if (!knownView(id)) return;
    setDeep(null); setView(id); setLauncher(false);
  };
  /** 화면을 열고, 그 화면이 id로 여는 항목을 지원하면 바로 연다 */
  const openItem = (v, id) => {
    if (!knownView(v)) return;
    setDeep(id && DEEP_VIEWS.includes(v) ? { view: v, id } : null); setView(v); setLauncher(false);
  };
  const openApproval = d => openItem('approval', d.id);
  /** 경로 'approval/<id>'·'leave'·'admin' 열기 — 해시 링크(Teams·메일·링크 복사)와 화면 알림(target)이 같은 함수를 쓴다. 모르는 화면이면 false */
  const openRoute = (route) => {
    const [v, id] = String(route || '').split('/');
    if (!v || !knownView(v)) return false;
    openItem(v, id);
    return true;
  };
  /** 알림 누르기(알림 목록·홈 공통) — 서버에 읽음을 남기고(실패하면 알림) 서버가 준 곳을 연다 */
  const openNotif = (n) => {
    if (n.unread) {
      api.post('/api/v1/notifications/read', { ids: [n.id] })
        .then(() => setNotifs(ns => ns.map(x => (x.id === n.id ? { ...x, unread: false } : x))))
        .catch(e => showToast('읽음을 저장하지 못했습니다 — ' + e.message));
    }
    if (n.target) openRoute(n.target);
  };
  // 알림(Teams·메일)·링크 복사의 링크: #/approval/<id>, #/board/<id>, #/org/<id>, #/booking/<id>, #/task/<id>, #/leave, #/admin
  React.useEffect(() => {
    const go = () => {
      let hash = window.location.hash;
      if (!hash) { try { hash = sessionStorage.getItem('ws-return') || ''; sessionStorage.removeItem('ws-return'); } catch { hash = ''; } }
      if (!openRoute((hash || '').replace(/^#\//, ''))) return;
      history.replaceState(null, '', window.location.pathname + window.location.search);   // 새로고침 때 다시 열리지 않게(?inTeams 등 쿼리는 남긴다 — 지우면 Teams 토큰을 못 붙인다)
    };
    go();
    window.addEventListener('hashchange', go);
    return () => window.removeEventListener('hashchange', go);
  }, []);

  // Teams 탭 딥링크(알림의 '열기' — context.subEntityId)로 들어오면 그 화면을 연다. 화면 목록(기능·역할)을 받은 뒤에 연다
  uE(() => {
    if (loadState !== 'ready' || !inTeams()) return;
    teamsSubPage().then(route => { if (route) openRoute(route); }).catch(() => { /* Teams 문맥을 못 받으면 첫 화면 그대로 */ });
  }, [loadState]);

  const unread = notifs.filter(n => n.unread).length;
  const inbox = approvalSummary.waiting;
  const narrow = prefs.sidebar === 'collapsed';

  // 새 알림·결재 배지: 화면이 보이는 동안 60초마다, 다시 보이거나 창으로 돌아올 때 바로 서버에서 받는다(계획 문서 5.2 2-7 결정 3 — 즉시 알림은 Teams·메일)
  uE(() => {
    if (loadState !== 'ready') return undefined;
    const refresh = () => {
      if (document.visibilityState !== 'visible') return;
      Promise.all([api.get('/api/v1/notifications'), api.get('/api/v1/approvals/summary'), api.get('/api/v1/ops-notices')])
        .then(([n, a, o]) => { setNotifs(mapNotifs(n.notifications)); setApprovalSummary(a); setOpsNotices(o.notices); setHomeTick(x => x + 1); })
        .catch(() => { /* 다음 주기에 다시 받는다 — 화면에 남은 값은 마지막으로 받은 서버 값 */ });
    };
    // 운영 공지는 loadAll에 없다 — 처음 한 번 받는다(실패하면 알리고, 다음 주기에 다시)
    api.get('/api/v1/ops-notices').then(o => setOpsNotices(o.notices)).catch(e => showToast('운영 공지를 받지 못했습니다 — ' + e.message));
    const timer = setInterval(refresh, 60_000);
    document.addEventListener('visibilitychange', refresh);
    window.addEventListener('focus', refresh);
    return () => { clearInterval(timer); document.removeEventListener('visibilitychange', refresh); window.removeEventListener('focus', refresh); };
  }, [loadState]);

  if (loadState !== 'ready') {
    return (
      <div style={{ height: '100vh', display: 'grid', placeItems: 'center', background: 'var(--bg)' }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 }}>
          <LogoMark size={38} />
          {loadState === 'loading'
            ? <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>불러오는 중…</div>
            : <>
                <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--crit)' }}>데이터를 불러오지 못했습니다</div>
                <div style={{ fontSize: 12, color: 'var(--ink-3)', maxWidth: 420, textAlign: 'center' }}>{loadError}</div>
                <Button onClick={() => window.location.reload()}>다시 시도</Button>
              </>}
        </div>
      </div>
    );
  }

  function renderView() {
    return (
      <React.Fragment>
        {view === 'home' && <Home onNav={nav} onOpen={openItem} onOpenApproval={openApproval} onOpenNotif={openNotif} onAllNotifs={() => setNotifOpen(true)} refreshKey={homeTick} onToast={showToast} notifs={notifs} />}
        {view === 'approval' && <Approval deepDoc={deep?.view === 'approval' ? deep : null} onToast={showToast} onChanged={refreshApprovals} />}
        {view === 'board' && <Board onToast={showToast} deepPost={deep?.view === 'board' ? deep : null} />}
        {view === 'org' && <Org deepPerson={deep?.view === 'org' ? deep : null} />}
        {view === 'booking' && <Booking onToast={showToast} deepBooking={deep?.view === 'booking' ? deep : null} />}
        {view === 'leave' && <Leave onToast={showToast} onChanged={refreshApprovals} />}
        {view === 'task' && <TaskView onToast={showToast} deepTask={deep?.view === 'task' ? deep : null} />}
        {view === 'mailagent' && FEATURES.mailAgent && <MailAgent onToast={showToast} />}
        {view === 'admin' && isAdmin() && <Admin onToast={showToast} />}
        {view === 'teams-lab' && knownView('teams-lab') && <TeamsLab onToast={showToast} />}
      </React.Fragment>
    );
  }

  // 팀·그룹 채팅 탭 구성 화면 — 어느 Workspace 화면을 탭으로 둘지 고른다
  if (teamsConfigMode()) return <TeamsTabConfig />;
  // Teams 대화창(봇·메시지 확장이 연 웹 대화창) — 사이드바·상단 없이 화면만, 닫기 버튼
  if (dialogMode()) {
    return (
      <div className="dialog-mode" style={{ minHeight: '100vh', background: 'var(--bg)' }}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '8px 16px', borderBottom: '1px solid var(--line)' }}>
          <Button size="sm" variant="secondary" onClick={() => { closeDialog().catch(() => window.close()); }}>닫기</Button>
        </div>
        {renderView()}
        {toast && <div className="toast"><Icon name="check-circle" size={14} color="var(--acc)" />{toast}</div>}
      </div>
    );
  }

  return (
    <div className="app" style={narrow ? { gridTemplateColumns: '64px 1fr' } : null}>
      <aside className="side">
        <div className="brand" style={narrow ? { justifyContent: 'center', padding: 0 } : null}>
          <WLogo size={20} />
          {!narrow && <div style={{ lineHeight: 1.25, minWidth: 0 }}>
            <div style={{ fontSize: 12.5, fontWeight: 700, letterSpacing: '-.02em', whiteSpace: 'nowrap' }}>FGCP Workspace</div>
            <div className="mono" style={{ fontSize: 9.5, color: 'var(--ink-3)', letterSpacing: '.04em', whiteSpace: 'nowrap' }}>사내 업무 포털</div>
          </div>}
        </div>
        <nav className="nav">
          {NAV.filter(n => navVisible(n) && !n.hidden).map((n, i) => {
            if (n.group) return narrow ? <div key={'g' + i} style={{ height: 1, background: 'var(--line)', margin: '10px 6px' }} /> : <div key={'g' + i} className="navlbl lbl">{n.group}</div>;
            const on = view === n.id;
            return (
              <button key={n.id} className={on ? 'on' : ''} onClick={() => nav(n.id)} title={n.label}
                style={narrow ? { justifyContent: 'center', padding: '9px 0' } : null}>
                <Icon name={n.icon} size={15} color={on ? 'var(--acc)' : 'var(--ink-3)'} />
                {!narrow && <span style={{ flex: 1, textAlign: 'left', whiteSpace: 'nowrap' }}>{n.label}</span>}
                {!narrow && n.badge === 'approval' && inbox > 0 && <span className="badge">{inbox}</span>}
                {narrow && n.badge === 'approval' && inbox > 0 && <span style={{ position: 'absolute', top: 6, right: 12, width: 5, height: 5, borderRadius: '50%', background: 'var(--crit)' }} />}
              </button>
            );
          })}
        </nav>
        <button className="who" onClick={() => nav('org')} style={narrow ? { justifyContent: 'center', padding: '12px 0' } : null}>
          <Avatar name={ME.name} size={26} />
          {!narrow && <span style={{ minWidth: 0 }}>
            <span style={{ display: 'block', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}>{ME.name} {ME.title}</span>
            <span className="mono" style={{ display: 'block', fontSize: 9.5, color: 'var(--ink-3)', whiteSpace: 'nowrap' }}>{ME.dept}</span>
          </span>}
        </button>
      </aside>

      <div className="main">
        <div className="top">
          <div className="crumb"><span>FGCP</span><i>/</i><b>{TITLES[view] || (APPS.find(a => a.id === view) || {}).name || '앱'}</b></div>
          <div className="search">
            <button onClick={() => setSearch(true)}>
              <Icon name="search" size={13} color="var(--ink-3)" />
              <span>검색</span>
              <span className="kbd" style={{ marginLeft: 'auto' }}>⌘K</span>
            </button>
          </div>
          <div className="tact">
            <SegmentedControl size="sm" aria-label="화면 밝기" value={prefs.theme} onChange={m => setPref('theme', m)}
              options={[{ value: 'dark', label: 'DARK' }, { value: 'light', label: 'LIGHT' }]} />
            <button className="ico" title="전체 앱" onClick={() => setLauncher(true)}><Icon name="grid" size={15} /></button>
            <div style={{ position: 'relative' }}>
              <button className="ico" title="알림" onClick={() => setNotifOpen(o => !o)}>
                <Icon name="bell" size={15} />{unread > 0 && <span className="pip" />}
              </button>
              {notifOpen && <NotifPanel notifs={notifs} setNotifs={setNotifs} onClose={() => setNotifOpen(false)} onOpen={openNotif} onToast={showToast} />}
            </div>
            <button className="ico" title="사이드바" onClick={() => setPref('sidebar', narrow ? 'expanded' : 'collapsed')}><Icon name={narrow ? 'chevron-right' : 'chevron-left'} size={15} /></button>
            <div style={{ position: 'relative' }}>
              <button className="ico" title="계정" onClick={() => setUserMenu(o => !o)}><Avatar name={ME.name} userId={ME.id} size={22} /></button>
              {userMenu && <UserMenu onClose={() => setUserMenu(false)} onOpenNotify={() => setNotifySettings(true)} />}
            </div>
          </div>
        </div>

        {opsNotices.filter(o => o.kind === 'incident' || o.kind === 'maintenance').map(o => (
          <button key={o.id} data-ops-banner={o.kind} onClick={() => nav('home')} style={{
            display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left', padding: '8px 24px', fontSize: 12.5,
            background: o.kind === 'incident' ? 'var(--crit-tint)' : 'var(--warn-tint)', color: o.kind === 'incident' ? 'var(--crit)' : 'var(--warn)',
            borderBottom: '1px solid var(--line)' }}>
            <b style={{ flex: 'none' }}>{OPS_KIND[o.kind].label}</b>
            <span style={{ flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: 'var(--ink)' }}>{o.title}</span>
            <span className="mono" style={{ flex: 'none', fontSize: 11, color: 'var(--ink-3)' }}>{opsPeriod(o)}</span>
          </button>
        ))}
        {renderView()}
      </div>

      {launcher && <Launcher onClose={() => { setLauncher(false); setHomeTick(n => n + 1); }} onNav={nav} onToast={showToast} />}
      {notifySettings && <NotifySettings onClose={() => setNotifySettings(false)} onToast={showToast} />}
      {search && <SearchOverlay onClose={() => setSearch(false)} onOpen={it => {
        setSearch(false);
        if (it.url) window.open(it.url, '_blank', 'noopener'); else openItem(it.view, it.id);
      }} />}
      {toast && <div className="toast"><Icon name="check-circle" size={14} color="var(--acc)" />{toast}</div>}

    </div>
  );
}

/**
 * 알림 목록 — 누르면 서버에 읽음을 남기고 서버가 준 곳(target)을 연다(shell.openNotif). '모두 읽음' 저장이 실패하면 알린다.
 */
function NotifPanel({ notifs, setNotifs, onClose, onOpen, onToast }) {
  const unread = notifs.filter(n => n.unread).length;
  const markRead = ids => api.post('/api/v1/notifications/read', ids ? { ids } : {})
    .then(() => setNotifs(ns => ns.map(n => !ids || ids.includes(n.id) ? { ...n, unread: false } : n)));
  const open = n => { onOpen(n); onClose(); };
  return (
    <React.Fragment>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 39 }} />
      <div className="dropdown" style={{ width: 344 }}>
        <div className="ph">
          <h3>알림</h3>
          {unread > 0 && <span className="mono" style={{ fontSize: 10.5, fontWeight: 600, color: 'var(--acc)' }}>{unread}</span>}
          <div className="r"><button className="link" onClick={() => markRead(null).catch(e => onToast('읽음을 저장하지 못했습니다 — ' + e.message))}>모두 읽음</button></div>
        </div>
        <div style={{ maxHeight: 360, overflowY: 'auto', padding: 6 }}>
          {notifs.map((n, i) => (
            <WAlert key={n.id} divider={i > 0}
              severity={n.unread ? (n.kind === 'approval' ? 'warn' : 'info') : 'resolved'}
              title={n.title} meta={[n.body.length > 24 ? n.body.slice(0, 24) + '…' : n.body, n.at]}
              onClick={() => open(n)} data-notif={n.id}
              style={{ cursor: 'pointer', background: n.unread ? 'var(--acc-tint)' : undefined }} />
          ))}
          {notifs.length === 0 && <Empty icon="bell" title="알림이 없습니다" />}
        </div>
      </div>
    </React.Fragment>
  );
}

function UserMenu({ onClose, onOpenNotify }) {
  const items = [['bell', '알림 설정', onOpenNotify]];
  return (
    <React.Fragment>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 39 }} />
      <div className="dropdown" style={{ width: 224 }}>
        <div style={{ padding: '12px 14px', borderBottom: '1px solid var(--line)' }}>
          <div style={{ fontSize: 12.5, fontWeight: 700, whiteSpace: 'nowrap' }}>{ME.name} {ME.title}</div>
          <div className="mono" style={{ fontSize: 10, color: 'var(--ink-3)', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{ME.email}</div>
        </div>
        <div style={{ padding: 6 }}>
          {items.map(([ic, l, act]) => (
            <button key={l} onClick={() => { act(); onClose(); }} style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '8px 10px', borderRadius: 'var(--r-sm)', fontSize: 12.5, fontWeight: 500, textAlign: 'left', color: 'var(--ink)' }}>
              <Icon name={ic} size={14} color="var(--ink-3)" />{l}
            </button>
          ))}
        </div>
        <div style={{ padding: 6, borderTop: '1px solid var(--line)' }}>
          <button onClick={() => { onClose(); void doLogout(); }} style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '8px 10px', borderRadius: 'var(--r-sm)', fontSize: 12.5, fontWeight: 500, color: 'var(--crit)', textAlign: 'left' }}>
            <Icon name="log-out" size={14} color="var(--crit)" />로그아웃
          </button>
        </div>
      </div>
    </React.Fragment>
  );
}

/**
 * 전체 앱 — 운영 중 서비스 전부(잠긴 것은 '잠김'과 담당자 — 권한은 담당자에게 요청), 상태(일부 장애·중단), 즐겨찾기(별 — 서버에 저장, 홈 '앱' 카드 순서).
 * 열 수 있는가·상태는 서버가 정한다(계획 문서 5.3 3c)
 */
function Launcher({ onClose, onNav, onToast }) {
  const [apps, setApps] = uS(APPS);
  const favs = apps.filter(a => a.favorite).map(a => a.id);
  const toggleFav = async app => {
    const next = app.favorite ? favs.filter(x => x !== app.id) : [...favs, app.id];
    try {
      await api.put('/api/v1/me/service-favorites', { slugs: next });
      const r = await api.get('/api/v1/services');
      applyServerData({ services: r.services });
      setApps(APPS);
    } catch (e) { onToast(e.message); }
  };
  return (
    <div className="ovl" onClick={onClose}>
      <div className="modal" style={{ width: 660, maxWidth: '92vw' }} onClick={e => e.stopPropagation()}>
        <div className="ph">
          <Icon name="grid" size={15} color="var(--acc)" />
          <h3>전체 앱</h3>
          <div className="r"><button className="ico" onClick={onClose} aria-label="닫기"><Icon name="x" size={15} /></button></div>
        </div>
        {APPS_ERROR && <div style={{ padding: '10px 16px 0', fontSize: 12, color: 'var(--crit)' }}>서비스 목록을 받지 못했습니다 — {APPS_ERROR}</div>}
        {/* 열 너비는 고정 비율(minmax(0, 1fr)) — 1fr만 쓰면 한 줄로 줄인 긴 설명이 열을 넓혀 오른쪽 카드가 모달 밖으로 잘렸다(2026-10-05) */}
        <div data-launcher style={{ padding: 16, display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 10 }}>
          {apps.map(app => {
            const health = app.health && HEALTH[app.health];
            return (
              <div key={app.id} data-app={app.id} data-access={app.locked ? 'locked' : 'open'} style={{ position: 'relative' }}>
                <button className="tile" disabled={!app.enabled} onClick={() => { if (!app.service) { onNav('approval'); return; } openService(app, onToast); }}
                  style={{ width: '100%', opacity: app.enabled ? 1 : .5, cursor: app.enabled ? 'pointer' : 'not-allowed', display: 'flex', flexDirection: 'column', gap: 8, padding: '14px 13px', textAlign: 'left' }}>
                  <AppIcon name={app.icon} size={17} color={app.enabled ? 'var(--acc)' : 'var(--ink-3)'} />
                  <span style={{ minWidth: 0 }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, fontWeight: 600, flexWrap: 'wrap' }}>
                      {app.name}{app.locked && <WPill tone="neutral" dot={false}>잠김</WPill>}{health && <WPill tone={health[1]} dot>{health[0]}</WPill>}
                    </span>
                    <span title={app.locked ? undefined : app.desc} style={{ display: 'block', fontSize: 10.5, color: 'var(--ink-3)', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {app.locked ? (app.owner ? `권한은 담당자(${app.owner})에게 요청` : '권한이 없습니다') : app.desc}</span>
                  </span>
                </button>
                {app.service && app.enabled && (
                  <button className="ico" aria-label={app.favorite ? `${app.name} 즐겨찾기 빼기` : `${app.name} 즐겨찾기`} aria-pressed={app.favorite}
                    onClick={() => toggleFav(app)} style={{ position: 'absolute', top: 6, right: 6 }}>
                    <Icon name="star" size={13} color={app.favorite ? 'var(--acc)' : 'var(--ink-3)'} />
                  </button>)}
              </div>
            );
          })}
        </div>
        <div style={{ padding: '11px 16px', borderTop: '1px solid var(--line)', display: 'flex', alignItems: 'center', gap: 7, fontSize: 11, color: 'var(--ink-3)', whiteSpace: 'nowrap' }}>
          <Icon name="shield" size={12} />권한이 있는 서비스는 한 번 로그인으로 열립니다 · 별을 누르면 홈 '앱' 카드에 먼저 보입니다
        </div>
      </div>
    </div>
  );
}

// 검색 결과 종류별 아이콘(서버가 준 종류 — domain/shell/search.ts)
const SEARCH_ICON = { person: 'users', approval: 'file-check', post: 'megaphone', booking: 'calendar', task: 'check-square', service: 'box' };

/** 통합 검색 — 서버 검색(모듈별 제공자가 권한 범위 안의 결과만 낸다). 입력이 멈추면 묻는다 */
function SearchOverlay({ onClose, onOpen }) {
  const [q, setQ] = uS('');
  const [groups, setGroups] = uS([]);
  const [state, setState] = uS('idle');   // idle | loading | done | error
  const [error, setError] = uS('');
  uE(() => {
    const k = q.trim();
    if (!k) { setGroups([]); setState('idle'); return undefined; }
    setState('loading');
    let alive = true;
    const t = setTimeout(() => {
      api.get('/api/v1/search?q=' + encodeURIComponent(k))
        .then(r => { if (alive) { setGroups(r.groups); setState('done'); } })
        .catch(e => { if (alive) { setError(e.message); setState('error'); } });
    }, 200);
    return () => { alive = false; clearTimeout(t); };
  }, [q]);
  const first = groups[0]?.items[0];
  return (
    <div className="ovl" onClick={onClose} style={{ paddingTop: '13vh' }}>
      <div className="modal" style={{ width: 580, maxWidth: '92vw' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '14px 16px', borderBottom: '1px solid var(--line)' }}>
          <Icon name="search" size={17} color="var(--ink-3)" />
          <input autoFocus value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && first) onOpen(first); }}
            placeholder="사람·결재·게시글·예약·업무·서비스 검색"
            style={{ flex: 1, border: 0, background: 'transparent', outline: 'none', font: 'inherit', fontSize: 14, color: 'var(--ink)' }} />
          <span className="kbd">ESC</span>
        </div>
        <div style={{ padding: 10, maxHeight: '60vh', overflowY: 'auto' }}>
          {state === 'idle' && <Empty icon="search" title="검색어를 입력하세요" sub="볼 수 있는 항목만 나옵니다" />}
          {state === 'error' && <Empty icon="search" title="검색하지 못했습니다" sub={error} />}
          {state === 'done' && groups.length === 0 && <Empty icon="search" title="결과가 없습니다" sub="다른 검색어를 입력해 보세요" />}
          {groups.map(g => (
            <React.Fragment key={g.type}>
              <div className="lbl" style={{ padding: '8px 10px 6px' }}>{g.label}</div>
              {g.items.map(it => (
                <button key={g.type + it.id} data-search={g.type} onClick={() => onOpen(it)}
                  style={{ display: 'flex', alignItems: 'center', gap: 11, width: '100%', padding: '9px 12px', borderRadius: 'var(--r-ctl)', textAlign: 'left' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--hover)'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <Icon name={SEARCH_ICON[g.type] ?? 'search'} size={14} color="var(--ink-3)" />
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: 'block', fontSize: 12.5, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{it.title}</span>
                    <span className="mono" style={{ display: 'block', fontSize: 10.5, color: 'var(--ink-3)' }}>{it.sub}</span>
                  </span>
                  {it.status && <StatusPill s={it.status} dot={false} />}
                  {it.url && <Icon name="arrow-up-right" size={13} color="var(--ink-3)" />}
                </button>
              ))}
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
}

export { App, Launcher, NAV, NotifPanel, SearchOverlay, TITLES, UserMenu };
